<template>
  <div id="pending-list">
    <vs-alert color="success" v-if="pendingBusinesses.length === 0">
      {{ $t('admin.not_pending_msg') }}
    </vs-alert>
    <div v-else>
      <div class="flex flex-wrap-reverse items-center flex-grow justify-between mb-base">
        <!-- ITEMS PER PAGE -->
        <vs-dropdown vs-trigger-click class="cursor-pointer">
          <div
            class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
          >
            <span class="mr-2">
              {{ (currentPage * pageSize - (pageSize - 1)) | persian_digits }} {{ $t('forms.to') }}
              {{
                businessDataCount - currentPage * pageSize > 0
                  ? currentPage * pageSize
                  : businessDataCount | persian_digits
              }}
              {{ $t('forms.from') }} {{ businessDataCount | persian_digits }}
            </span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item @click="pageSize = 5">
              <span>{{ 5 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 10">
              <span>{{ 10 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 50">
              <span>{{ 50 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 100">
              <span>{{ 100 | persian_digits }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>
      <div v-for="business in businessData" :key="business.id">
        <vx-card class="mb-base" :label="business.id">
          <div class="vx-row">
            <div class="vx-col" id="avatar-col">
              <div class="img-container mb-4">
                <avatar :id="business.user.avatar" size="50px" class="mr-1" />
              </div>
            </div>
            <div class="vx-col flex-1 mt-2">
              <div class="font-semibold">{{ $t('businesses.manager') }}</div>
              <div>{{ business.user.details.firstName }} {{ business.user.details.lastName }}</div>
            </div>
            <div class="vx-col" v-if="['pending', 'unspecified'].includes(business.state)">
              <vs-button
                size="small"
                color="success"
                icon="icon-check"
                icon-pack="feather"
                class="w-full"
                type="flat"
                @click.stop="changeBusinessState(business, 'approved')"
              >
                {{ $t('admin.confirm_pending') }}
              </vs-button>
              <vs-button
                size="small"
                color="danger"
                icon="icon-x"
                icon-pack="feather"
                class="w-full"
                type="flat"
                @click.stop="changeBusinessState(business, 'rejected')"
              >
                {{ $t('admin.reject_pending') }}
              </vs-button>
            </div>
          </div>
          <div class="vx-row m-3">
            <div class="vx-col md:w-1/4 mb-4">
              <div class="font-semibold">{{ $t('entity.state') }}</div>
              <div>{{ $t('entity.states.' + business.state) }}</div>
            </div>
            <div class="vx-col md:w-1/4 mb-4">
              <div class="font-semibold">{{ $t('businesses.plans.plan') }}</div>
              <div>{{ $t('businesses.plans.' + business.plan) }}</div>
            </div>
            <div class="vx-col md:w-1/4 mb-4">
              <div class="font-semibold">{{ $t('businesses.website') }}</div>
              <div>{{ business.website }}</div>
            </div>
            <div class="vx-col md:w-1/4 mb-4">
              <div class="font-semibold">{{ $t('businesses.expiration_date') }}</div>
              <div>{{ business.expirationDate | datetime }}</div>
            </div>
          </div>
          <vs-collapse class="m-3" type="border" v-if="business.contracts || business.agreements">
            <vs-collapse-item v-if="business.contracts">
              <div slot="header" class="font-semibold" style="font-size: 14px">
                {{ $t('businesses.contracts') }}
              </div>
              <div>
                <p class="mb-2" v-for="(contract, index) in business.contracts" :key="index">
                  {{ contract }}
                </p>
              </div>
            </vs-collapse-item>
            <vs-collapse-item v-if="business.agreements">
              <div slot="header" class="font-semibold" style="font-size: 14px">
                {{ $t('businesses.agreements') }}
              </div>
              <div class="vx-row p-3">
                <p class="mb-2" v-for="(agreement, index) in business.agreements" :key="index">
                  {{ agreement }}
                </p>
              </div>
            </vs-collapse-item>
          </vs-collapse>
          <div v-if="business.places.length > 0">
            <h3 class="m-3">{{ $t('admin.places_to_confirm') }}</h3>
            <vs-table ref="table" :data="business.places" class="vs-table places-table">
              <template slot="thead">
                <vs-th sort-key="alias">{{ $t('places.alias') }}</vs-th>
                <vs-th sort-key="subject">{{ $t('places.subject') }}</vs-th>
                <vs-th sort-key="category">{{ $t('places.category') }}</vs-th>
                <vs-th sort-key="state">{{ $t('entity.state') }}</vs-th>
                <vs-th sort-key="status">{{ $t('places.status') }}</vs-th>
                <vs-th sort-key="status">{{ $t('places.employee_percent') }}</vs-th>
                <vs-th class="center">{{ $t('forms.actions') }}</vs-th>
              </template>

              <template slot-scope="{ data }">
                <tbody id="table-body" v-if="data.length">
                  <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                    <vs-td :data="tr.alias">{{ tr.alias }}</vs-td>
                    <vs-td :data="tr.subject">{{ $t(`places.subjects.${tr.subject}`) }}</vs-td>
                    <vs-td :data="tr.category">{{ $t(`places.categories.${tr.category}`) }}</vs-td>
                    <vs-td :data="tr.state">{{ $t(`entity.states.${tr.state}`) }}</vs-td>
                    <vs-td :data="tr.status">{{ $t(`places.statuses.${tr.status}`) }}</vs-td>
                    <vs-td :data="tr.employeePercent">{{ tr.employeePercent }}</vs-td>
                    <vs-td>
                      <template v-if="tr.state !== 'approved'">
                        <vs-button
                          size="small"
                          color="success"
                          icon="icon-check"
                          icon-pack="feather"
                          class="w-full"
                          type="flat"
                          @click.stop="changePlaceState(tr, 'approved')"
                        >
                          {{ $t('admin.confirm_pending') }}
                        </vs-button>
                        <vs-button
                          size="small"
                          color="danger"
                          icon="icon-x"
                          icon-pack="feather"
                          class="w-full"
                          type="flat"
                          @click.stop="changePlaceState(tr, 'rejected')"
                        >
                          {{ $t('admin.reject_pending') }}
                        </vs-button>
                      </template>
                    </vs-td>

                    <template slot="expand">
                      <div class="w-full">
                        <div class="vx-row w-full">
                          <div class="vx-col w-full md:w-1/4">
                            <div class="font-semibold">{{ $t('users.addresses.address') }}:</div>
                            <address-string :address="tr" hide-label />
                            <div class="font-semibold">{{ $t('users.location') }}:</div>
                            <div>{{ $t('users.lat') }}: {{ tr.location.lat | persian_digits }}</div>
                            <div>{{ $t('users.lon') }}: {{ tr.location.lon | persian_digits }}</div>
                          </div>
                          <div class="vx-col w-full md:w-3/4">
                            <open-street-map
                              view-only
                              :markers="[{ latlng: tr.location, name: tr.alias }]"
                              :zoom="15"
                              disable-fit
                              :center="tr.location"
                            />
                          </div>
                        </div>
                        <div class="vx-row w-full mt-2" v-if="tr.description">
                          <div class="vx-col">
                            <div class="font-semibold">{{ $t('places.description') }}:</div>
                            <div>{{ tr.description }}</div>
                          </div>
                        </div>
                        <div class="vx-row mt-2">
                          <div class="font-semibold">{{ $t('places.attachments') }}:</div>
                          <div v-for="(file, indextr) in tr.attachments" :key="indextr">
                            <vs-button
                              @click="downloadFile(file)"
                              class="mx-1 vs-con-loading__container"
                              :id="`file-${file}`"
                              type="border"
                              size="small"
                              :disabled="disable_files.includes(file)"
                            >
                              {{ $t('comments.download') }}{{ (indextr + 1) | persian_digits }}
                            </vs-button>
                          </div>
                        </div>
                      </div>
                    </template>
                  </vs-tr>
                </tbody>
              </template>
            </vs-table>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import moduleBusiness from '@/store/business/moduleBusiness'
import modulePlaces from '@/store/places/modulePlaces'
import moduleUserManagement from '@/store/user-management/moduleUserManagement'
import AddressString from '@/views/components/maps/AddressString'
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import Avatar from '@/views/components/common/Avatar'

export default {
  name: 'BusinessPendingList',
  components: {
    AddressString,
    OpenStreetMap,
    Avatar
  },
  data() {
    return {
      pendingBusinesses: [],
      pageSize: 10,
      skip: 0,
      disable_files: []
    }
  },
  methods: {
    downloadFile(fileId) {
      this.$vs.loading({
        container: `#file-${fileId}`,
        scale: 0.4
      })
      this.disable_files.push(fileId)
      this.$store.dispatch('file/downloadFile', fileId).then(response => {
        this.disable_files.splice(
          this.disable_files.findIndex(f => f === fileId),
          1
        )
        this.$vs.loading.close(`#file-${fileId} > .con-vs-loading`)
        window.open(response, '_blank')
      })
    },
    changeBusinessState(business, state) {
      this.$vs.loading()
      this.$store
        .dispatch('business/editBusinessState', { id: business._id, state })
        .then(() => {
          business.state = state
        })
        .finally(() => this.$vs.loading.close())
    },
    changePlaceState(place, state) {
      this.$vs.loading()
      this.$store
        .dispatch('place/editPlaceState', { id: place._id, state })
        .then(() => {
          place.state = state
        })
        .finally(() => this.$vs.loading.close())
    }
  },
  computed: {
    businessDataCount() {
      return this.pendingBusinesses.length
    },
    businessData() {
      return this.pendingBusinesses.slice(this.skip, this.skip + this.pageSize)
    },
    totalPages() {
      return Math.ceil(this.pendingBusinesses.length / this.pageSize)
    },
    currentPage: {
      get() {
        return Math.ceil(this.skip / this.pageSize + 1)
      },
      set(val) {
        this.skip = (val - 1) * this.pageSize
      }
    }
  },
  created() {
    this.$vs.loading()
    if (!moduleBusiness.isRegistered) {
      this.$store.registerModule('business', moduleBusiness)
      moduleBusiness.isRegistered = true
    }
    if (!modulePlaces.isRegistered) {
      this.$store.registerModule('place', modulePlaces)
      modulePlaces.isRegistered = true
    }
    if (!moduleUserManagement.isRegistered) {
      this.$store.registerModule('userManagement', moduleUserManagement)
      moduleUserManagement.isRegistered = true
    }

    this.$store.commit('business/SET_BUSINESSES_QUERY', {})
    this.$store
      .dispatch('business/fetchBusinessesWithPlaceState', 'pending')
      .then(res => {
        const data = JSON.parse(JSON.stringify(res.data))
        if (data.length === 0) {
          this.$vs.loading.close()
          this.pendingBusinesses = []
        }
        let businesses = []
        let count = 0
        data.forEach(business => {
          this.$store
            .dispatch('userManagement/fetchUser', business.userId)
            .then(user => {
              businesses.push({ ...business, user: user.data })
              count += 1
              if (count === data.length) {
                this.pendingBusinesses = businesses
                this.$vs.loading.close()
              }
            })
            .catch(() => this.$vs.loading.close())
        })
      })
      .catch(() => this.$vs.loading.close())
  }
}
</script>

<style lang="scss">
#pending-list {
  table:not(.vs-table) {
    td {
      vertical-align: top;
      min-width: 140px;
      padding-bottom: 0.8rem;
      word-break: break-all;
    }
  }

  .card-map-top {
    overflow: hidden;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }
}
</style>
